/**
 * Copyright IBM Corp. 2022, 2026
 */

import classNames from 'classnames'
import {
	createElement,
	type ComponentPropsWithRef,
	type ElementType,
	type ReactNode,
} from 'react'
import s from './style.module.scss'

const TEXT_COLORS = [
	'primary',
	'strong',
	'faint',
	'disabled',
	'high-contrast',
	'action',
	'action-hover',
	'action-active',
	'highlight',
	'highlight-on-surface',
	'highlight-high-contrast',
	'success',
	'success-on-surface',
	'success-high-contrast',
	'warning',
	'warning-on-surface',
	'warning-high-contrast',
	'critical',
	'critical-on-surface',
	'critical-high-contrast',
] as const

const TEXT_GROUPS = [
	'hds-body',
	'code',
	'body',
	'display-expressive',
	'label',
] as const

const TEXT_SIZES = [
	'100',
	'200',
	'300',
	'400',
	'500',
	'600',
	'700',
	'800',
] as const

const TEXT_WEIGHTS = ['regular', 'medium', 'semibold', 'bold'] as const

type TextColor = (typeof TEXT_COLORS)[number]
type TextWeight = (typeof TEXT_WEIGHTS)[number]
type TextGroup = (typeof TEXT_GROUPS)[number]
type TextSize = (typeof TEXT_SIZES)[number]

// small typescript hack to support autocomplete and arbitrary strings
type HexColor = string & { hexish?: unknown }

interface BaseTextProps {
	size?: TextSize
	weight?: TextWeight
	align?: 'left' | 'center' | 'right'
	color?: TextColor | HexColor
	className?: string
	children?: ReactNode
}

type PolymorphicTextProps<
	TTag extends ElementType,
	OwnProps extends object,
> = OwnProps & {
	tag?: TTag
} & Omit<ComponentPropsWithRef<TTag>, keyof OwnProps | 'tag'>

interface TextImplInternalProps extends BaseTextProps {
	group: TextGroup
	tag?: ElementType
	// allow any other intrinsic props to pass through
	[key: string]: unknown
}

function getTypographyToken(group: TextGroup, size: TextSize): string {
	const groupToTokenMap: Record<TextGroup, string> = {
		'hds-body': `mds-typography-legacy-body-${size}`,
		code: `mds-typography-code-${size}`,
		'display-expressive': `mds-typography-display-expressive-${size}`,
		label: `mds-typography-label`,
		body: `mds-typography-body-${size}`,
	}

	return groupToTokenMap[group]
}

const TextImpl = (props: TextImplInternalProps) => {
	const {
		tag,
		group,
		size = '200',
		weight,
		align,
		color,
		children,
		className,
		...rest
	} = props

	const Component = (tag ?? 'span') as ElementType
	const variant = getTypographyToken(group, size)

	const usesPredefinedColor = TEXT_COLORS.includes(color as TextColor)

	return (
		<Component
			className={classNames(
				variant,
				{
					[s[`align-${align}`]]: align,
					[`mds-typography-font-weight-${weight}`]: weight,
					[`mds-foreground-${color}`]: color && usesPredefinedColor,
				},
				className,
			)}
			style={color && !usesPredefinedColor ? { color } : {}}
			{...rest}
		>
			{children}
		</Component>
	)
}

/** ------------------------------------------------------------------
 * Body
 * ------------------------------------------------------------------ */

interface BodyOwnProps extends BaseTextProps {
	size?: '200' | '300' | '400'
}

export type BodyProps<TTag extends ElementType = 'span'> = PolymorphicTextProps<
	TTag,
	BodyOwnProps
>

const Body = <TTag extends ElementType = 'span'>(props: BodyProps<TTag>) => {
	const { tag, size = '300', ...rest } = props

	return createElement(TextImpl, {
		...rest,
		tag: (tag ?? 'span') as ElementType,
		size,
		group: 'body' as const,
	})
}

/** ------------------------------------------------------------------
 * DisplayExpressive
 * ------------------------------------------------------------------ */

interface DisplayExpressiveOwnProps extends BaseTextProps {
	size?: '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800'
}

export type DisplayExpressiveProps<TTag extends ElementType = 'span'> =
	PolymorphicTextProps<TTag, DisplayExpressiveOwnProps>

const DisplayExpressive = <TTag extends ElementType = 'span'>(
	props: DisplayExpressiveProps<TTag>,
) => {
	const { tag, size = '200', ...rest } = props

	return createElement(TextImpl, {
		...rest,
		tag: (tag ?? 'span') as ElementType,
		size,
		group: 'display-expressive' as const,
	})
}

/** ------------------------------------------------------------------
 * Label
 * ------------------------------------------------------------------ */

type LabelOwnProps = Omit<BaseTextProps, 'size'>

export type LabelProps<TTag extends ElementType = 'span'> =
	PolymorphicTextProps<TTag, LabelOwnProps>

const Label = <TTag extends ElementType = 'span'>(props: LabelProps<TTag>) => {
	const { tag, ...rest } = props

	return createElement(TextImpl, {
		...rest,
		tag: (tag ?? 'span') as ElementType,
		group: 'label' as const,
	})
}

/** ------------------------------------------------------------------
 * HDSBody
 * ------------------------------------------------------------------ */

interface HDSBodyOwnProps extends BaseTextProps {
	size?: '100' | '200' | '300'
}

export type HDSBodyProps<TTag extends ElementType = 'span'> =
	PolymorphicTextProps<TTag, HDSBodyOwnProps>

const HDSBody = <TTag extends ElementType = 'span'>(
	props: HDSBodyProps<TTag>,
) => {
	const { tag, size = '200', weight = 'regular', ...rest } = props

	return createElement(TextImpl, {
		...rest,
		tag: (tag ?? 'span') as ElementType,
		size,
		weight,
		group: 'hds-body' as const,
	})
}

/** ------------------------------------------------------------------
 * Code
 * ------------------------------------------------------------------ */

interface CodeOwnProps extends BaseTextProps {
	size?: '100' | '200' | '300'
}

export type CodeProps<TTag extends ElementType = 'span'> = PolymorphicTextProps<
	TTag,
	CodeOwnProps
>

const Code = <TTag extends ElementType = 'span'>(props: CodeProps<TTag>) => {
	const { tag, size = '200', weight = 'regular', ...rest } = props

	return createElement(TextImpl, {
		...rest,
		tag: (tag ?? 'span') as ElementType,
		size,
		weight,
		group: 'code' as const,
	})
}

const Text = {
	Body,
	DisplayExpressive,
	Label,
	HDSBody,
	Code,
}

export { Text, TEXT_COLORS, TEXT_WEIGHTS }
